import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { map, isEqual } from "lodash";
import "./AIAbstractSubmission.scss";

import {
  addAiAbstractStepData,
  addAiSubmitAbstract,
  fetchAiAbstractData,
  fetchCurrentPaper,
  fetchUserEventDetails,
  getKeywords,
  showSuccess,
  storeAiAbstractData,
  throwError,
  throwSuccess,
  uploadPaperSubmission,
} from "store/slices";

import {
  getDataFromLocalStorage,
  objectToFormData,
  getCountryCodeName,
  getPhoneNumberLength,
} from "utils/helpers";

import NewAbstractBasicDetails from "../NewAbstractSubmission/NewAbstractBasicDetails";
import PaperDetails from "./StepComponents/PaperDetails";
import AuthorInformation from "./StepComponents/AuthorInformation";
import RelevantKeynoteSpeakers from "./StepComponents/RelevantKeynoteSpeakers";
import AddOnsDetails from "./StepComponents/AddOnsDetails";
import ReviewAbstract from "./StepComponents/ReviewAbstract";
import AbstractSubmitSuccessPopup from "../AbstractSubmitSuccessPopup";
import UploadAbstract from "./StepComponents/UploadAbstract";
import { errorMsgForMobile } from "utils/constants";
import { useCallback, useEffect, useState } from "react";
import CoAuthorInformation from "./StepComponents/CoAuthorInformation";
import PrimaryAndSecondaryCoAuthors from "./StepComponents/PrimaryAndSecondaryCoAuthors";

const AIAbstractSubmission = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const { eventData } = useSelector((state) => ({
    eventData: state.global.eventData,
  }));

  const userData = getDataFromLocalStorage();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSuccess, setIsSuccess] = useState(false);
  const [keywordsList, setKeywordsList] = useState({ existing: [], custom: [] });
  const [btnLoading, setBtnLoading] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [aiAbstractId, setAiAbstractId] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  //  Helper: Mark all error fields as touched
  const touchAllFields = (formikProps, errors) => {
    const touchedFields = {};
    Object.keys(errors).forEach((key) => {
      touchedFields[key] = true;
    });
    formikProps.setTouched(touchedFields);
  };

  //  Initial Values
  const initialValues = {
    paper_title: formValues?.title || "",
    paper_abstract: formValues?.abstract || "",
    paper_keywords: formValues?.keywords?.join(",") || "",
    authors: formValues?.authors || "",
    authors_email: formValues?.author_email || "",
    author_name: formValues?.author_name || "",
    // formValues?.authors === "" ? formValues?.author_email : formValues?.author_name || "",
    co_authors: formValues?.co_authors || [],
    primary_co_author: formValues?.primary_co_author || '',
    secondary_co_author: formValues?.secondary_co_author || "",
    abstract_path: formValues?.abstract_file || "",
    presentation_type: formValues?.presentation_type || "",
    submission_type: formValues?.submission_type || "",
    submission_category: formValues?.submission_category || "",
    source: formValues?.source || "",
    email_id: formValues?.author_email || formValues?.email_id || "",
    university: formValues?.university || '',
    country: formValues?.country || "",
    contact_number: formValues?.contact_number || "",
    whatsapp_number: formValues?.whatsapp_number || "",
    country_code:
      formValues?.country_code ||
      getCountryCodeName(userData?.country_code || "IN") ||
      "IN",
    mobile_no_length:
      Number(formValues?.mobile_no_length) ||
      getPhoneNumberLength(userData?.country_code) ||
      10,
    relevant_keynote_speakers: formValues?.relevant_keynote_speakers || [],
    add_on_services: {
      services: (() => {
        const svc = formValues?.add_on_services?.services;
        const servicesArray = Array.isArray(svc)
          ? svc
          : svc?.services ?? [];
        return servicesArray.map((service) => ({
          ...service,
          isChecked: service.isChecked ?? false,
        }));
      })(),
    },
    conference_track: formValues?.conference_track || "",
    sdg_goals: formValues?.sdg_goals,
    sdg_introduction: formValues?.sdg_introduction,
    sdg_name: formValues?.sdg_name,
    sdg_percentage: formValues?.sdg_percentage,
    author_orcid_id: formValues?.author_orcid_id || '',
    author_scopus_id: formValues?.author_scopus_id || "",
  };

  const validationSchemas = [
    // Step 1: Upload
    Yup.object()
      .shape({
        abstract_path: Yup.string().nullable(),
        full_paper_path: Yup.string().nullable(),
      })
      .test(
        "file-required",
        "Either Abstract or Full Paper is required.",
        (values) => Boolean(values?.abstract_path || values?.full_paper_path)
      ),

    // Step 2: Paper + Author Info
    Yup.object().shape({
      paper_title: Yup.string().required("Paper title is required."),
      paper_abstract: Yup.string().required("Abstract is required."),
      // paper_keywords: Yup.string().test(
      //   "paper_keywords-length",
      //   "You must enter at least 3 and up to 10 Keywords",
      //   function (value) {
      //     const keywordArray = value
      //       ? value.split(",").filter((kw) => kw.trim() !== "")
      //       : [];
      //     return keywordArray.length >= 3 && keywordArray.length <= 10;
      //   }
      // ),
      presentation_type: Yup.string().required("Presentation type is required."),
      email_id: Yup.string()
        .email("Email must be a valid email")
        .required("Email is required."),
      country: Yup.string().required("Country is required."),
      contact_number: Yup.lazy((_, obj) => {
        const { country_code, mobile_no_length } = obj?.parent || {};
        if (country_code === "IN" && mobile_no_length) {
          const validLengths = Array.isArray(mobile_no_length)
            ? mobile_no_length
            : [mobile_no_length];
          return Yup.string()
            .required("Phone number is required.")
            .test(
              "valid-length",
              `Phone number must be ${validLengths.join(" or ")} digits.`,
              (value) => value && validLengths.includes(value.length)
            );
        } else {
          return Yup.string()
            .required("Phone number is required.")
            .min(8, errorMsgForMobile(8)?.min)
            .max(15, errorMsgForMobile(15)?.max);
        }
      }),
      // submission_type: Yup.string().required("Submission type is required."),
      submission_category: Yup.string().required("Submission category is required."),
      authors: Yup.string().when("is_email", {
        is: false,
        then: (schema) => schema.required("Author is required."),
        otherwise: (schema) => schema,
      }),
      authors_email: Yup.string().when("is_email", {
        is: true,
        then: (schema) =>
          schema
            .required("Author email is required.")
            .email("Email must be a valid email"),
        otherwise: (schema) => schema,
      }),
      co_authors: Yup.array().of(
        Yup.object().shape({
          id: Yup.string().nullable(),
          name: Yup.string().required("Co-author name is required"),
          email: Yup.string()
            .email("Co-author email must be valid")
            .required("Co-author email is required"),
          country: Yup.string().required("Country is required"),
          country_code: Yup.string().required("Country code is required"),
          phone: Yup.string()
            .required("Co-author phone is required")
            .when('country_code', {
              is: (country_code) => country_code === 'IN',
              then: (schema) => schema
                .min(10, "Phone number must be 10 digits for India")
                .max(10, "Phone number must be 10 digits for India")
                .matches(/^[0-9]{10}$/, "Phone number must be 10 digits for India"),
              otherwise: (schema) => schema
                .min(8, "Phone number must be at least 8 digits")
                .max(15, "Phone number cannot exceed 15 digits")
                .matches(/^[0-9]+$/, "Phone number must contain only numbers")
            }),
        })
      ),
      primary_co_author: Yup.string().when("co_authors", {
        is: (co_authors) => Array.isArray(co_authors) && co_authors.length >= 2,
        then: Yup.string()
          .required("Primary Co-author is required.")
          .test(
            "different-co-authors",
            "Primary & Secondary Co-authors cannot be the same. Please select different co-authors and review the note below.",
            function (value) {
              const { secondary_co_author } = this.parent;
              return !(value && secondary_co_author && value === secondary_co_author);
            }
          ),
        otherwise: Yup.string().nullable(),
      }),

      secondary_co_author: Yup.string().when("co_authors", {
        is: (co_authors) => Array.isArray(co_authors) && co_authors.length >= 2,
        then: Yup.string()
          .required("Secondary Co-author is required.")
          .test(
            "different-co-authors",
            "Primary & Secondary Co-authors cannot be the same. Please select different co-authors and review the note below.",
            function (value) {
              const { primary_co_author } = this.parent;
              return !(value && primary_co_author && value === primary_co_author);
            }
          ),
        otherwise: Yup.string().nullable(),
      }),
    }),

    // Step 3: Relevant Keynote Speakers (string validation)
    Yup.object().shape({
      conference_track: Yup.string().required('Please select a conference track'),
    }),
  ];

  // Fetch Keywords
  const fetchKeywords = async () => {
    const response = await dispatch(getKeywords());
    const listArray = response?.data || [];
    setKeywordsList({
      existing: map(listArray, (elm) => ({
        id: elm?.keywords,
        label: elm?.keywords,
      })),
      custom: [],
    });
  };

  // Fetch Event Details
  const fetchEventDetails = async () => {
    await dispatch(
      fetchUserEventDetails({
        event_id: params?.eventId,
        user_id: getDataFromLocalStorage("id"),
      })
    );
  };

  // Fetch Saved Data
  const getAiAbstractData = async (id) => {
    if (!id) return;
    try {
      setIsLoading(true);
      setAiAbstractId(id);
      const response = await dispatch(fetchAiAbstractData(id));
      if (response?.status === 200 && response?.data) {
        const data = response.data;
        setFormValues(data);
        const extraKeywords =
          data?.keywords?.map((kw) => ({ id: kw, label: kw })) || [];
        setKeywordsList((prev) => ({
          existing: prev.existing,
          custom: extraKeywords,
        }));
        setCurrentStep(data.stage || 1);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Get Current Paper ID
  const getCurrentPaperId = async () => {
    try {
      setIsLoading(true);
      await dispatch(storeAiAbstractData({}));
      const payload = {
        event_id: params?.eventId,
        user_id: getDataFromLocalStorage("id"),
      };
      const response = await dispatch(fetchCurrentPaper(payload));
      if (response?.data) {
        await getAiAbstractData(response.data.id);
        setCurrentStep(response?.data?.stage || 1);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Initial API calls
  useEffect(() => {
    fetchKeywords();
    fetchEventDetails();
    getCurrentPaperId();
  }, [params?.eventId]);



  // Step save
  const handleSaveAbstractData = async (values, step) => {
    setBtnLoading(true);
    const response = await dispatch(
      addAiSubmitAbstract(
        objectToFormData({
          stage: step,
          event_id: params.eventId,
          user_id: getDataFromLocalStorage("id"),
          ...values,
          ai_submission_id: aiAbstractId,
          co_authors: JSON.stringify(values?.co_authors),
          ...(formValues?.abstract_id && { abstract_id: formValues.abstract_id }),
        })
      )
    );
    if (response?.data) {
      setCurrentStep((s) => s + 1);
      setFormValues(prev => {
        if (!prev) return response?.data;
        return {
          ...prev,
          abstract_id: response?.data?.abstract_id,
          ...response?.data,
        };
      });
    }
    setBtnLoading(false);
  };


  const handleAiAbstractStepSubmit = async (values, step) => {
    setBtnLoading(true);
    const response = await dispatch(
      addAiAbstractStepData(
        objectToFormData({
          stage: step,
          event_id: params.eventId,
          user_id: getDataFromLocalStorage("id"),
          ...values,
          id: aiAbstractId,
          abstract_id: formValues?.abstract_id,
          co_authors: JSON.stringify(values?.co_authors),
          add_on_services: JSON.stringify(values?.add_on_services),
        })
      )
    );
    if (response?.data) {
      if (step === 4 || step === 2) getAiAbstractData(aiAbstractId);
      if (step === 5) {
        setIsSuccess(true)
        dispatch(showSuccess(response?.message))
      }
      else {
        setCurrentStep((s) => s + 1);
      }
    }
    setBtnLoading(false);
  };


  // Back button handler
  const handleBackClick = async (formikProps) => {
    setCurrentStep((s) => s - 1);
  };

  const renderStep = (formikProps) => {
    const { values } = formikProps;
    const commonProps = { ...formikProps, btnLoading };

    switch (currentStep) {
      case 1:
        return (
          <UploadAbstract
            {...commonProps}
            onNext={async () => {
              const errors = await formikProps.validateForm();
              touchAllFields(formikProps, errors);
              const { abstract_path, full_paper_path } = values;
              if (!Object.keys(errors).length && (abstract_path || full_paper_path) && !formValues?.ai_abstract_id) {
                const fileValue = abstract_path || full_paper_path;
                const abstract_type = abstract_path ? "abstract" : "full-paper";
                setBtnLoading(true);
                const formData = objectToFormData({
                  abstract_file: fileValue,
                  event_id: params?.eventId,
                  submitted_from: "dashboard",
                  type: abstract_type,
                });
                const response = await dispatch(uploadPaperSubmission(formData));
                if (response?.status === 200) {
                  dispatch(throwSuccess(response?.message));
                  setCurrentStep((s) => s + 1);
                  getAiAbstractData(response?.data?.id);
                  formikProps.setValues({
                    ...values,
                    abstract_path: "",
                    full_paper_path: "",
                  });
                } else {
                  formikProps.setValues({
                    ...values,
                    abstract_path: "",
                    full_paper_path: "",

                  });
                  dispatch(throwError({ message: response?.message }));
                }
                setBtnLoading(false);
              }
              else if (aiAbstractId) {
                setCurrentStep((s) => s + 1);

              }
            }}
          />
        );

      case 2:
        return (
          <>
            <PaperDetails
              {...commonProps}
              keywordsList={keywordsList}
              setKeywordsList={setKeywordsList}
            />
            <AuthorInformation {...commonProps} />
            <CoAuthorInformation  {...commonProps} />

            <PrimaryAndSecondaryCoAuthors
              {...commonProps}
              onNext={async () => {
                const errors = await formikProps.validateForm();
                touchAllFields(formikProps, errors);
                if (Object.keys(errors).length === 0) {
                  await handleSaveAbstractData(values, currentStep);
                }
              }}
              onBack={() => handleBackClick(formikProps)}
            />
          </>
        );

      case 3:
        return (
          <RelevantKeynoteSpeakers
            {...commonProps}
            onNext={async () => {
              const errors = await formikProps.validateForm();
              touchAllFields(formikProps, errors);
              if (Object.keys(errors).length === 0) {
                await handleAiAbstractStepSubmit(values, currentStep);
              }
            }}
            onBack={() => handleBackClick(formikProps)}
          />
        );

      case 4:
        return (
          <AddOnsDetails
            {...commonProps}
            onNext={() => handleAiAbstractStepSubmit(values, currentStep)}
            onBack={() => handleBackClick(formikProps)}
          />
        );

      case 5:
        return (
          <ReviewAbstract
            {...commonProps}
            onNext={() => handleAiAbstractStepSubmit(values, currentStep)}
            onBack={() => handleBackClick(formikProps)}
            eventData={eventData}
          />
        );

      default:
        return <div>Page Not Found</div>;
    }
  };

  return isSuccess ? (
    <AbstractSubmitSuccessPopup
      isSuccess={isSuccess}
      onHide={() => setIsSuccess(false)}
      submission_type={formValues?.submission_type}
      eventData={eventData}
      abstract_id={formValues?.abstract_id}
    />
  ) : (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchemas[currentStep - 1]}
      enableReinitialize={true}
    >
      {(formikProps) => (
        <Form>
          <div id="ai-abstract-submission-container">
            {currentStep !== 5 && <NewAbstractBasicDetails eventData={eventData} submission_type={formValues?.submission_type} />}
            {isLoading ? (
              <div className="loader-container">
                <div className="spinner"></div>
              </div>
            ) : (
              renderStep(formikProps)
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default AIAbstractSubmission;
